import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
const SHARED_DATA = {
  business_name: "Cyradon Capital",
  logo_src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1563866/img/cyradon_capital_logo.png"
};
function useScrollThreshold(threshold = 20) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > threshold);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [threshold]);
  return scrolled;
}
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-sans text-[13px] uppercase tracking-[0.1em] text-foreground hover:text-secondary transition-colors duration-300 font-medium">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background border-b border-border shadow-lg lg:hidden flex flex-col p-6 gap-4 animate-in slide-in-from-top-5 fade-in duration-200">
      <nav className="flex flex-col gap-4">
        {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-sans text-sm uppercase tracking-widest text-foreground hover:text-secondary py-2 border-b border-border/50 last:border-0" onClick={onClose}>
            {item.label}
          </Link>)}
        <div className="pt-4">
          <Button variant="ghost" className="w-full justify-center border border-secondary text-foreground hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 uppercase tracking-widest text-xs h-10" asChild>
            <Link to="/portal">Private Portal</Link>
          </Button>
        </div>
      </nav>
    </div>;
}
export default function ExecutiveNavigation() {
  const isScrolled = useScrollThreshold(20);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full transition-all duration-500 border-b ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm" : "bg-background border-transparent"}`}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/" className="block">
            <img src={SHARED_DATA.logo_src} alt={SHARED_DATA.business_name} className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center px-8">
          <MenuProvider menu_id="30">
            <DesktopNav />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex flex-shrink-0">
          <Button variant="ghost" className="border border-secondary text-foreground hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 uppercase tracking-widest text-[11px] h-9 px-6 font-medium" asChild>
            <Link to="/portal">Private Portal</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-transparent hover:text-secondary transition-colors" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="30">
        <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}